const ROOT_SELECTOR = '[data-assistance-component]';
const TAB_SELECTOR = '[data-assistance-component-tab]';
const PANEL_SELECTOR = '[data-assistance-component-panel]';
const HEADER_SELECTOR = '.assistance-component__header';
const LAYOUT_SELECTOR = '.assistance-component__layout';
const DESKTOP_MQ = '(min-width: 1150px)';
const STUCK_CLASS = 'assistance-component__header--is-stuck';
const PINNED_CLASS = 'assistance-component__header--is-pinned-bottom';

export function initAssistanceComponent(): void {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach(initAssistanceComponentRoot);
}

function initAssistanceComponentRoot(root: HTMLElement): void {
  initAssistanceComponentTabs(root);
  initAssistanceComponentStickyHeader(root);
}

function initAssistanceComponentTabs(root: HTMLElement): void {
  const tabs = root.querySelectorAll<HTMLButtonElement>(TAB_SELECTOR);
  const panels = root.querySelectorAll<HTMLElement>(PANEL_SELECTOR);

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const id = tab.getAttribute('data-assistance-component-tab');
      if (!id) return;

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('assistance-component__toggle-btn--active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-assistance-component-panel') === id;
        panel.classList.toggle('assistance-component__panel--hidden', !isActive);
      });

      root.dispatchEvent(new CustomEvent('assistance-component:update'));
    });
  });
}

function initAssistanceComponentStickyHeader(root: HTMLElement): void {
  const header = root.querySelector<HTMLElement>(HEADER_SELECTOR);
  const layout = root.querySelector<HTMLElement>(LAYOUT_SELECTOR);
  if (!header || !layout) return;

  const mq = window.matchMedia(DESKTOP_MQ);
  let raf = 0;
  let columnLeft = 0;
  let columnWidth = 0;

  const getStickyTop = (): number => {
    const siteHeader = document.querySelector<HTMLElement>('[data-site-header]');
    return siteHeader ? siteHeader.getBoundingClientRect().height + 16 : 32;
  };

  const resetHeader = (): void => {
    header.classList.remove(STUCK_CLASS, PINNED_CLASS);
    header.style.removeProperty('position');
    header.style.removeProperty('top');
    header.style.removeProperty('left');
    header.style.removeProperty('width');
    header.style.removeProperty('z-index');
  };

  const measureColumn = (): void => {
    const wasStuck = header.classList.contains(STUCK_CLASS);
    if (wasStuck) {
      resetHeader();
    }

    const rect = header.getBoundingClientRect();
    columnLeft = rect.left;
    columnWidth = rect.width;
  };

  const updateStickyHeader = (): void => {
    if (!mq.matches) {
      resetHeader();
      return;
    }

    const stickyTop = getStickyTop();
    const layoutRect = layout.getBoundingClientRect();
    const headerHeight = header.offsetHeight;

    if (layoutRect.top >= stickyTop) {
      resetHeader();
      measureColumn();
      return;
    }

    if (layoutRect.bottom <= headerHeight + stickyTop) {
      resetHeader();
      header.classList.add(PINNED_CLASS);
      return;
    }

    if (!columnWidth) {
      measureColumn();
    }

    header.classList.remove(PINNED_CLASS);
    header.classList.add(STUCK_CLASS);
    header.style.position = 'fixed';
    header.style.top = `${stickyTop}px`;
    header.style.left = `${columnLeft}px`;
    header.style.width = `${columnWidth}px`;
    header.style.zIndex = '2';
  };

  const scheduleStickyUpdate = (): void => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(updateStickyHeader);
  };

  mq.addEventListener('change', scheduleStickyUpdate);
  window.addEventListener('scroll', scheduleStickyUpdate, { passive: true });
  window.addEventListener('resize', () => {
    measureColumn();
    scheduleStickyUpdate();
  });
  root.addEventListener('assistance-component:update', () => {
    measureColumn();
    scheduleStickyUpdate();
  });

  measureColumn();
  scheduleStickyUpdate();
}
